import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil, tap } from 'rxjs/operators';

import { LEARNING_HUB_LANGUAGES, LEARNING_HUB_MODULE_PATH } from '../../definitions/learning-hub.constants';
import {
  LearningHubArticleMeta,
  LearningHubCategories,
  LearningHubCategory,
  LearningHubIndex,
  LearningHubLanguage,
  LearningHubLanguageOption
} from '../../definitions/learning-hub.models';
import { LearningHubService } from '../../services/learning-hub.service';
import { categoryCommands, navigationSections, resolveLanguage, staffPickArticles } from '../../utils/learning-hub.utils';

interface HomeSection {
  category: LearningHubCategory;
  link: string[];
}

/**
 * Home of the Learning Hub, same structure as learning-brands.rappi.com.co: welcome block with the centered
 * search ("Start learning today"), "Search by Section" with the 8 top-level sections and the "Staff Pick".
 */
@Component({
  selector: 'app-learning-hub-home',
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent implements OnInit, OnDestroy {
  readonly modulePath = LEARNING_HUB_MODULE_PATH;
  readonly languageOptions: LearningHubLanguageOption[] = LEARNING_HUB_LANGUAGES.map((lang: LearningHubLanguage) => ({
    lang,
    available: true
  }));

  lang: LearningHubLanguage;
  sections: HomeSection[] = [];
  staffPicks: LearningHubArticleMeta[] = [];
  loading = true;
  error = false;

  private readonly DESTROY$ = new Subject<void>();

  constructor(
    private readonly _route: ActivatedRoute,
    private readonly _router: Router,
    private readonly _translate: TranslateService,
    private readonly _learningHubService: LearningHubService
  ) {}

  ngOnInit(): void {
    this._route.queryParamMap
      .pipe(
        map((params: ParamMap) =>
          resolveLanguage(params.get('lang'), this._translate.currentLang, this._translate.defaultLang)
        ),
        tap((lang: LearningHubLanguage) => {
          this.lang = lang;
          this.loading = true;
          this.error = false;
        }),
        switchMap((lang: LearningHubLanguage) =>
          combineLatest([this._learningHubService.getCategories(), this._learningHubService.getIndex(lang)]).pipe(
            map(([tree, index]: [LearningHubCategories, LearningHubIndex]) => ({ lang, tree, index })),
            catchError(() => of(null))
          )
        ),
        takeUntil(this.DESTROY$)
      )
      .subscribe((data: { lang: LearningHubLanguage; tree: LearningHubCategories; index: LearningHubIndex } | null) => {
        this.loading = false;
        if (!data) {
          this.error = true;
          this.sections = [];
          this.staffPicks = [];
          return;
        }
        this.sections = navigationSections(data.tree)
          .filter((category: LearningHubCategory) => (category.articles[data.lang] || []).length > 0)
          .map((category: LearningHubCategory) => ({
            category,
            link: categoryCommands(category.key, data.tree.categories)
          }));
        this.staffPicks = staffPickArticles(data.tree, data.index.articles, data.lang);
      });
  }

  ngOnDestroy(): void {
    this.DESTROY$.next();
    this.DESTROY$.complete();
  }

  changeLanguage(option: LearningHubLanguageOption): void {
    this._router.navigate([], { relativeTo: this._route, queryParams: { lang: option.lang } });
  }

  trackByKey(_index: number, section: HomeSection): string {
    return section.category.key;
  }
}
